import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useState } from 'react'

import {
  AcknowledgmentSignFlow,
  AcknowledgmentStatusDialog,
} from 'partner360/features/acknowledgmentStatusDialog'

import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import { useNextOfferingBlockedAction } from '../../../components/nextOfferingAction'

interface IUsePlanSignFlowParams {
  partnerId: string
  fYear: number
  offering: string | null
}

/**
 * `Acknowledge` from a step: the plan review and the reader's signature right here, as the
 * completeness detail signs. `start` takes the row to sign, of any commitment type; without one
 * the reader's PBM row is signed. Where the reader may not sign that row, the plan's own
 * acknowledgment window opens instead. Signing moves the plan's status, so the cockpit refetches.
 */
export const usePlanSignFlow = ({ partnerId, fYear, offering }: IUsePlanSignFlowParams) => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<'idle' | 'sign' | 'window'>('idle')
  const [isSigning, setIsSigning] = useState(false)
  const [entryId, setEntryId] = useState<number | undefined>()

  const start = useCallback((rowEntryId?: number) => {
    setEntryId(rowEntryId)
    setStep('sign')
  }, [])
  const close = useCallback(() => setStep('idle'), [])
  const openWindow = useCallback(() => setStep('window'), [])
  const handleUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])
  const blockedAction = useNextOfferingBlockedAction(offering, close)

  const element =
    partnerId && offering ? (
      <>
        {step === 'sign' && (
          <AcknowledgmentSignFlow
            partnerId={partnerId}
            fYear={fYear}
            offering={offering}
            entryId={entryId}
            onClose={close}
            onUnavailable={openWindow}
            onBusyChange={setIsSigning}
            onUpdated={handleUpdated}
          />
        )}

        <AcknowledgmentStatusDialog
          show={step === 'window'}
          partnerId={partnerId}
          fYear={fYear}
          offering={offering}
          onClose={close}
          onUpdated={handleUpdated}
          blockedAction={blockedAction}
        />
      </>
    ) : null

  return { start, isSigning, element }
}
